import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, Search, Target, Camera as Instagram, Briefcase as Linkedin,
  MessageSquare, Briefcase, Star, ThumbsUp, MessageCircle as Twitter, TrendingDown, Zap,
  Trash2, Loader2
} from 'lucide-react';
import { api } from '../lib/gapApi';

export const Dashboard = () => {
  const navigate = useNavigate();
  const [totalAnalyses, setTotalAnalyses] = useState(0);
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this saved analysis? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await api.delete(`/new-analysis/${id}`);
      setRecentAnalyses((prev) => prev.filter((a) => a.id !== id));
      setTotalAnalyses((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to delete analysis', err);
      alert('Failed to delete analysis: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Dashboard "Recent Analyses" shows New Analysis (multi-source) runs only —
        // General/Instagram/LinkedIn/etc. each have their own history on their own tab.
        const { data } = await api.get('/new-analysis/history?limit=5');
        setTotalAnalyses(data.length);
        setRecentAnalyses(data);
      } catch (error) {
        console.error("Failed to fetch dashboard stats", error);
      }
    };
    fetchStats();
  }, []);

  const QuickLaunchCard = ({ to, title, icon: Icon, colorClass, borderClass, shadowClass }) => (
    <Link
      to={to}
      className={`group relative bg-slate-900/40 backdrop-blur-xl p-6 rounded-3xl border border-slate-700/50 hover:${borderClass} transition-all duration-300 hover:-translate-y-2 hover:${shadowClass} flex flex-col items-center justify-center text-center`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${colorClass} opacity-0 group-hover:opacity-10 transition-opacity rounded-3xl`} />
      <div className={`p-4 rounded-2xl mb-4 bg-slate-800/50 group-hover:scale-110 transition-transform duration-300 ${colorClass.split(' ')[0].replace('from-', 'text-')}`}>
        <Icon size={32} />
      </div>
      <h3 className="font-bold text-slate-200 group-hover:text-white transition-colors">{title}</h3>
      <div className="mt-2 text-xs font-medium text-slate-500 uppercase tracking-widest flex items-center gap-1 group-hover:text-slate-400">
        Launch <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto animate-in fade-in slide-in-from-bottom-8 relative overflow-hidden">

      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="flex items-center justify-between gap-4 mb-10 relative z-10">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 via-teal-300 to-indigo-400 mb-2">
            Command Center
          </h1>
          <p className="text-slate-400">Overview of your Gap Analysis intelligence engine.</p>
        </div>
        <Link
          to="/analyzer/analysis/new"
          className="hidden md:flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all"
        >
          <Zap size={18} /> Deep Insight Engine
        </Link>
      </div>

      {/* Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 relative z-10">
        <div className="group relative bg-slate-900/40 backdrop-blur-xl p-8 rounded-3xl border border-slate-700/50 hover:border-emerald-500/50 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(16,185,129,0.1)] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <h3 className="text-slate-400 font-medium mb-3 uppercase tracking-widest text-xs relative z-10">Total Analyses</h3>
          <p className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400 relative z-10">{totalAnalyses}</p>
        </div>
        <div className="group relative bg-slate-900/40 backdrop-blur-xl p-8 rounded-3xl border border-slate-700/50 hover:border-indigo-500/50 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(99,102,241,0.1)] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <h3 className="text-slate-400 font-medium mb-3 uppercase tracking-widest text-xs relative z-10">Data Sources Active</h3>
          <p className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400 relative z-10">8</p>
        </div>
        <div className="group relative bg-slate-900/40 backdrop-blur-xl p-8 rounded-3xl border border-slate-700/50 hover:border-teal-500/50 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(20,184,166,0.1)] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <h3 className="text-slate-400 font-medium mb-3 uppercase tracking-widest text-xs relative z-10">System Status</h3>
          <div className="flex items-center gap-3 relative z-10 mt-1">
            <span className="w-4 h-4 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_15px_rgba(16,185,129,0.8)]" />
            <p className="text-4xl font-bold text-emerald-400">Online</p>
          </div>
        </div>
      </div>

      {/* Quick Launch Grid */}
      <div className="mb-16 relative z-10">
        <h2 className="text-2xl font-bold text-white mb-6">Intelligence Tools</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <QuickLaunchCard
            to="/analyzer/gap-analysis"
            title="General AI Gap"
            icon={Target}
            colorClass="from-indigo-500 to-purple-500"
            borderClass="border-indigo-500/50"
            shadowClass="shadow-[0_20px_40px_rgba(99,102,241,0.15)]"
          />
          <QuickLaunchCard
            to="/analyzer/instagram-analysis"
            title="Instagram"
            icon={Instagram}
            colorClass="from-fuchsia-500 to-pink-500"
            borderClass="border-fuchsia-500/50"
            shadowClass="shadow-[0_20px_40px_rgba(217,70,239,0.15)]"
          />
          <QuickLaunchCard
            to="/analyzer/linkedin-analysis"
            title="LinkedIn"
            icon={Linkedin}
            colorClass="from-blue-500 to-cyan-500"
            borderClass="border-blue-500/50"
            shadowClass="shadow-[0_20px_40px_rgba(59,130,246,0.15)]"
          />
          <QuickLaunchCard
            to="/analyzer/reddit-analysis"
            title="Reddit"
            icon={MessageSquare}
            colorClass="from-orange-500 to-amber-500"
            borderClass="border-orange-500/50"
            shadowClass="shadow-[0_20px_40px_rgba(249,115,22,0.15)]"
          />
          <QuickLaunchCard
            to="/analyzer/glassdoor-analysis"
            title="Glassdoor"
            icon={Briefcase}
            colorClass="from-emerald-500 to-teal-500"
            borderClass="border-emerald-500/50"
            shadowClass="shadow-[0_20px_40px_rgba(16,185,129,0.15)]"
          />
          <QuickLaunchCard
            to="/analyzer/g2-analysis"
            title="G2"
            icon={Star}
            colorClass="from-red-500 to-rose-500"
            borderClass="border-red-500/50"
            shadowClass="shadow-[0_20px_40px_rgba(239,68,68,0.15)]"
          />
          <QuickLaunchCard
            to="/analyzer/trustpilot-analysis"
            title="Trustpilot"
            icon={ThumbsUp}
            colorClass="from-green-500 to-lime-500"
            borderClass="border-green-500/50"
            shadowClass="shadow-[0_20px_40px_rgba(34,197,94,0.15)]"
          />
          <QuickLaunchCard
            to="/analyzer/twitter-analysis"
            title="Twitter"
            icon={Twitter}
            colorClass="from-slate-400 to-gray-500"
            borderClass="border-slate-500/50"
            shadowClass="shadow-[0_20px_40px_rgba(148,163,184,0.15)]"
          />
        </div>
      </div>

      <div className="flex justify-between items-center mb-8 pb-4 border-b border-slate-700/50 relative z-10">
        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
          Recent Analyses
        </h2>
        <Link to="/analyzer/history" className="group text-emerald-400 hover:text-emerald-300 flex items-center gap-2 font-medium text-sm uppercase tracking-wider transition-colors">
          View all history <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      {recentAnalyses.length === 0 ? (
        <div className="bg-slate-900/40 backdrop-blur-xl p-16 rounded-3xl border border-slate-700/50 text-center shadow-2xl relative z-10">
          <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6 border border-slate-700/50 shadow-inner">
            <Search className="text-slate-500" size={32} />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">No Analysis History</h3>
          <p className="text-slate-400 mb-8 max-w-md mx-auto">You haven't run any analyses yet. Start by analyzing a company's digital presence to generate deep insights and proposals.</p>
          <Link to="/analyzer/analysis/new" className="inline-flex items-center space-x-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold py-4 px-8 rounded-xl shadow-[0_10px_20px_rgba(16,185,129,0.2)] hover:shadow-[0_15px_30px_rgba(16,185,129,0.3)] hover:-translate-y-1 transition-all">
            <span>Start a New Analysis</span>
            <ArrowRight size={20} />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
          {recentAnalyses.map((analysis) => (
            <div
              key={analysis.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/analyzer/analysis/new?historyId=${analysis.id}`)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/analyzer/analysis/new?historyId=${analysis.id}`); } }}
              className="group bg-slate-900/40 backdrop-blur-xl p-6 rounded-3xl border border-slate-700/50 hover:border-emerald-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_15px_30px_rgba(16,185,129,0.1)] cursor-pointer"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center border border-slate-700/50 group-hover:border-emerald-500/30 transition-colors">
                  <TrendingDown className="text-emerald-400" size={24} />
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                    Completed
                  </span>
                  <button
                    type="button"
                    onClick={(e) => handleDelete(e, analysis.id)}
                    disabled={deletingId === analysis.id}
                    aria-label="Delete this analysis"
                    title="Delete"
                    className="p-1.5 rounded-lg text-slate-500 opacity-0 group-hover:opacity-100 hover:text-rose-400 hover:bg-rose-500/10 transition-all disabled:opacity-50"
                  >
                    {deletingId === analysis.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  </button>
                </div>
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-300 transition-colors">{analysis.company_name}</h3>
              <p className="text-slate-400 text-sm mb-4 line-clamp-2">{analysis.website || 'Website N/A'}</p>
              <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                <span>{new Date(analysis.created_at).toLocaleDateString()}</span>
                <span className="flex items-center gap-1 group-hover:text-emerald-400 transition-colors">
                  View Report <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
